<template>
  <!--
    Used to edit the Resorts 
  -->
  <Portlet
    :title="$t('create') + ' Resort'"
    icon="plus"
    class="mb-4"
  >
    <LoadingPlaceholder v-if="loading" />
    <div
      v-else
      class="resortDetails"
    >
      <template v-if="resort">
        <h4>Resort Information</h4>
        <div class="ml-4 mt-4">
          <div class="form-group row mb-4">
            <label class="col-12 col-md-2 mt-2">{{ $t("id") }}</label>
            <div
              v-tooltip="{
                content: tooltipText,
                placement: 'bottom',
                classes: ['info'],
                offset: 5,
                delay: {
                  hide: 300,
                },
              }"
              class="col-12 col-md-3"
            >
              <input
                v-model="resort.id"
                v-focus
                :class="[
                  'form-control m-input m-input--air',
                  { 'is-invalid': !isIdValid },
                ]"
                type="number"
              />
              <span
                v-show="errors.has('Id')"
                class="badge badge-danger"
              >{{
                errors.first("Id")
              }}</span>
              <span
                v-show="!isIdValid"
                class="ml-2 badge badge-danger"
              >{{
                $t("resortForm.setId")
              }}</span>
            </div>
            <div class="col-12 col-md-7" />
          </div>
          <div class="form-group row mb-4">
            <label class="col-12 col-md-2 mt-2">{{ $t("key") }}</label>
            <div class="col-12 col-md-3">
              <input
                v-model="resort.resortKey"
                v-focus
                class="form-control m-input m-input--air"
                type="text"
              />
            </div>
            <div class="col-12 col-md-7" />
          </div>
          <div class="form-group row mb-4">
            <label class="col-12 col-md-2 mt-2">{{ $t("Short Name") }}</label>
            <div class="col-12 col-md-3">
              <input
                v-model="resort.attributes.short_name"
                class="form-control m-input m-input--air"
                type="text"
              />
            </div>
            <div class="col-12 col-md-7" />
          </div>
          <div class="form-group row mb-4">
            <label class="col-12 col-md-2 mt-2">Timezone</label>
            <div class="col-12 col-md-3">
              <select
                v-model="resort.timeZoneId"
                :class="['form-control m-input m-input--air']"
              >
                <option
                  v-for="(tz, index) in timezones"
                  :key="`tz-spot-${index}`"
                  :value="tz"
                >
                  {{ `${tz}` }}
                </option>
              </select>
            </div>
            <div class="col-12 col-md-7" />
          </div>
          <div class="form-group row mb-4">
            <label class="col-12 col-md-2 mt-2">Latitude</label>
            <div class="col-12 col-md-3">
              <input
                v-model="resort.location.latitude"
                class="form-control m-input m-input--air"
                type="text"
              />
            </div>
            <div class="col-12 col-md-7" />
          </div>
          <div class="form-group row mb-4">
            <label class="col-12 col-md-2 mt-2">Longitude</label>
            <div class="col-12 col-md-3">
              <input
                v-model="resort.location.longitude"
                class="form-control m-input m-input--air"
                type="text"
              />
            </div>
            <div class="col-12 col-md-7" />
          </div>
          <div class="form-group row mb-4">
            <label class="col-12 col-md-2 mt-2">Altitude</label>
            <div class="col-12 col-md-3">
              <input
                v-model="resort.location.altitude"
                class="form-control m-input m-input--air"
                type="text"
              />
            </div>
            <div class="col-12 col-md-7" />
          </div>
          <div class="form-group row mb-4">
            <label class="col-12 col-md-2 mt-2">Country Code</label>
            <div class="col-12 col-md-3">
              <input
                v-model="resort.countryCode"
                class="form-control m-input m-input--air"
                type="text"
              />
            </div>
            <div class="col-12 col-md-7" />
          </div>
          <div class="form-group row mb-4">
            <label class="col-12 col-md-2 mt-2">Provinces</label>
            <div class="col-12 col-md-3">
              <input
                v-model="resortProvinces"
                class="form-control m-input m-input--air"
                type="text"
              />
            </div>
            <div class="col-12 col-md-7" />
          </div>
          <div class="form-group row mb-4">
            <label class="col-12 col-md-2 mt-2">H ISO</label>
            <div class="col-12 col-md-3">
              <input
                v-model="resort.attributes.h_iso"
                class="form-control m-input m-input--air"
                type="text"
              />
            </div>
            <div class="col-12 col-md-7" />
          </div>
          <div class="form-group row mb-4">
            <label class="col-12 col-md-2 mt-2">H MAX</label>
            <div class="col-12 col-md-3">
              <input
                v-model="resort.attributes.h_max"
                class="form-control m-input m-input--air"
                type="text"
              />
            </div>
            <div class="col-12 col-md-7" />
          </div>
          <div class="form-group row mb-4">
            <label class="col-12 col-md-2 mt-2">H MIN</label>
            <div class="col-12 col-md-3">
              <input
                v-model="resort.attributes.h_min"
                class="form-control m-input m-input--air"
                type="text"
              />
            </div>
            <div class="col-12 col-md-7" />
          </div>
          <div class="form-group row mb-4">
            <label class="col-12 col-md-2 mt-2">Weather Url</label>
            <div class="col-12 col-md-3">
              <input
                v-model="resort.attributes.weather_url"
                class="form-control m-input m-input--air"
                type="text"
              />
            </div>
            <div class="col-12 col-md-7" />
          </div>
          <div class="form-group row mb-4">
            <label class="col-12 col-md-2 mt-2">Company Name</label>
            <div class="col-12 col-md-3">
              <input
                v-model="resort.attributes.company_name"
                class="form-control m-input m-input--air"
                type="text"
              />
            </div>
            <div class="col-12 col-md-7" />
          </div>
          <LoadingPlaceholder v-if="flowLoading" />
          <div
            v-else
            class="form-group row mb-4"
          >
            <label class="col-12 col-md-2 mt-2">Flow</label>
            <div class="col-12 col-md-3">
              <select
                v-model="flow.clazz"
                :class="['form-control m-input m-input--air']"
              >
                <option
                  v-for="(flw, index) in flows"
                  :key="`flw-spot-${index}`"
                  :value="flw.id"
                >
                  {{ `${flw.value}` }}
                </option>
              </select>
            </div>
            <div class="col-12 col-md-7" />
          </div>
          <div class="form-group row mb-4">
            <label class="col-12 col-md-2 mt-2">Visible</label>
            <div class="col-12 col-md-1">
              <input
                v-model="resort.visible"
                class="form-control m-input mt-2"
                type="checkbox"
              />
            </div>
            <div class="col-12 col-md-7" />
          </div>
          <div class="form-group row mb-4">
            <label class="col-12 col-md-2 mt-2">Active</label>
            <div class="col-12 col-md-1">
              <input
                v-model="resort.active"
                class="form-control m-input mt-2"
                type="checkbox"
              />
            </div>
            <div class="col-12 col-md-7" />
          </div>
          <div class="form-group row mb-4">
            <label class="col-12 col-md-2 mt-2">Composite</label>
            <div class="col-12 col-md-1">
              <input
                v-model="resort.composite"
                class="form-control m-input mt-2"
                type="checkbox"
              />
            </div>
            <div class="col-12 col-md-7" />
          </div>
          <div class="form-group row mb-4">
            <label class="col-12 col-md-2 mt-2">Has Skiline Chart</label>
            <div class="col-12 col-md-1">
              <input
                v-model="resort.hasSkilineChart"
                class="form-control m-input mt-2"
                type="checkbox"
              />
            </div>
            <div class="col-12 col-md-7" />
          </div>
          <button
            class="btn btn-sm btn-primary mt-4 float-right"
            :disabled="!isIdValid"
            @click="addResort"
          >
            <font-awesome-icon
              class="mr-2"
              icon="upload"
            />{{ $t("create") }}
          </button>
          <div class="clearfix" />
        </div>
      </template>
      <template v-else>
        {{ $t("noDataAvailable") }}
      </template>
    </div>
  </Portlet>
</template>

<script>
import { errorMixin } from "@/mixins/errorMixin.js";
import { dateTimeMixin } from "@/mixins/dateTimeMixin.js";
import { VPopover } from "v-tooltip";

export default {
  name: "AddResort",
  mixins: [errorMixin, dateTimeMixin, VPopover],
  data() {
    return {
      resort: {
        attributes: {},
        location: {
          latitude: 0,
          longitude: 0,
          altitude: 0,
        },
        hasSkilineChart: false,
        active: false,
        visible: false,
      },
      isIdValid: false,
      tooltipText:
        "1xx Salzburg Steiermark \n " +
        "2xx Kärnten \n " +
        "3xx Tirol Osttirol \n " +
        "4xx Vorarlberg, Niederösterreich, Oberösterrreich \n " +
        "5xx Italien \n " +
        "6xx Schweiz \n " +
        "7xx Frankreich, Spanien, Skandinawien \n " +
        "8xx Deutschland, Slowakei, Tschechien, Rest of World \n " +
        "9xx Japan, Rest of World",
      flowLoading: false,
      loading: false,
      flow: {},
      flows: [
        { id: "skidata.DefaultSkidataFlow", value: "Skidata TDI" },
        {
          id: "multi.DefaultSkidataSwisspassFlow",
          value: "Skidata TDI Swisspass",
        },
        { id: "axess.DefaultAxessFlow", value: "Axess DCI4WTP" },
        { id: "dss.DefaultDssFlow", value: "DSS MyData" },
        { id: "skidata.DefaultSkidataDazFlow", value: "Skidata DAZ" },
        { id: "multi.DefaultSkidataFeratelFlow", value: "Skidata TDI Feratel" },
        { id: "token.DefaultSkilineTokenFlow", value: "Skiline Token" },
        { id: "multi.DefaultSkidataAxessFlow", value: "Skidata Axess" },
        { id: "multi.DefaultAxessSwisspassFlow", value: "Axess Swisspass" },
        {
          id: "multi.DefaultSkidataOccAxessSwisspassFlow",
          value: "Skidata OCC Axess Swisspass",
        },
        { id: "baqueira.DefaultBaqueiraFlow", value: "Baqueira" },
        { id: "multi.DefaultAxessFeratelFlow", value: "Axess Feratel" },
        {
          id: "multi.DefaultSkidataFeratelSwisspassFlow",
          value: "Skidata Feratel Swisspass",
        },
      ],
    };
  },
  computed: {
    timezones() {
      return this.dateTime_IANA_Timezones();
    },
    resortProvinces: {
      get() {
        if (this.resort.provinces) {
          return this.resort.provinces.join(",");
        }
        return "";
      },
      set(val) {
        this.resort.provinces = val
          .split(",")
          .map((x) => x.trim())
          .filter((x) => x.length > 0);
      },
    },
  },
  watch: {
    "resort.id": function (val) {
      if (val.length == 3) {
        this.checkIfIdIsValid();
      } else {
        this.isIdValid = false;
      }
    },
  },
  methods: {
    addResort() {
      this.loading = true;
      this.axios
        .post(`/Resort/AddResort`, this.resort)
        .then((response) => {
          if (response && response.status == 200) {
            this.error_clear();
            this.$snotify.success("Successfully created Resort!");
            this.upsertFlow();
          }
        })
        .finally(() => {
          this.reloadResortCache();
        })
        .catch((error) => {
          this.error_clear();
          this.error_validate(error);
          this.loading = false;
        });
    },
    async reloadResortCache() {
      this.axios
        .post(`/Resort/ReloadResortCache`)
        .then(() => {})
        .catch(() => {
          this.$snotify.success(
            "Reload Resort Cache not successfull - changes are not yet applied to the system!"
          );
        });
    },
    async checkIfIdIsValid() {
      this.axios
        .get(`/Resort/CheckIfIdIsValid?resortId=${this.resort.id}`)
        .then((response) => {
          this.isIdValid = response.data;
          if (this.isIdValid) {
            this.getFlow();
          }
        });
    },
    async getFlow() {
      this.flowLoading = true;
      this.axios
        .get(`/Resort/GetFlow?id=${this.resort.id}`)
        .then((response) => {
          if (response.data) {
            this.flow = response.data;
          }
        })
        .finally(() => {
          this.flowLoading = false;
        });
    },
    async upsertFlow() {
      if (!this.flow.clazz) {
        return
      }
      this.flowLoading = true;
      const found = this.flows.find((x) => x.id == this.flow.clazz);
      this.flow.name = found.value;
      if (!this.flow.id) {
        this.flow.resortId = this.resort.id;
        this.axios
          .post(`/Resort/CreateFlow`, this.flow)
          .then((response) => {
            if (response && response.status == 200) {
              this.flow = response.data;
              this.$router.push("/resorts");
            }
          })
          .finally(() => {
            this.flowLoading = false;
          });
      } else {
        this.axios.put(`/Resort/UpdateFlow`, this.flow).then((response) => {
          this.flowLoading = false;
          if (response && response.status == 200) {
            this.$router.push("/resorts");
          }
        });
      }
    },
  },
};
</script>

<style>
.tooltip.info .tooltip-inner {
  line-break: auto;
  white-space: pre-line;
}

.tooltip.popover .popover-inner {
  background: white;
  padding: 24px;
  border-radius: 5px;
  box-shadow: 0 5px 30px rgba(white, 0.1);
  max-width: 1000px;
}

.popover-arrow {
  border-color: white;
}
.tooltip-arrow {
  z-index: 1;
}
</style>
